import { Component, OnInit } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { MatDialogRef } from '@angular/material/dialog';
import { Observable, Subject, catchError, distinctUntilChanged, finalize, map, of, switchMap, takeUntil, tap } from 'rxjs';
import { AuthService } from 'src/app/services/auth.service';
import { NotificationService } from 'src/app/services/notification.service';
import { CerereTypeService } from '../cerere-type.service';
import { CerereTypeAddPayload } from '../cerere-type.interface';
import { environment } from 'src/environments/environment';
import { helper } from 'src/environments/helper';

import { UserOrganigramaSearchPayload } from 'src/app/interfaces/common.interface';
import { Generic } from 'src/app/interfaces/generic.interface';
import { CommonService } from 'src/app/services/common.service';
import { CererePriorityService } from 'src/app/platform/nomenclatoare/cerere-priority/cerere-priority.service';
import { StatusService } from 'src/app/platform/nomenclatoare/cerere-status/status.service';

@Component({
    selector: 'app-add-cerere-type',
    templateUrl: './add-cerere-type.component.html',
    styleUrls: ['./add-cerere-type.component.scss'],
})
export class AddCerereTypeComponent implements OnInit {
    // general error
    errorTitle: string = environment.config.customNotifications.headers.error;
    errorIcon: string = environment.config.customNotifications.icons.error;
    errorType: string = environment.config.customNotifications.icons.error;
    helper = helper;
    token!: string;
    isLoading = false;

    dgaspc_id = environment.interop.dgaspcId;

    form: FormGroup = new FormGroup({
        name: new FormControl('', [Validators.required]),
        slug: new FormControl('', [Validators.required]),
        priority_id: new FormControl('-1', [Validators.required, Validators.min(0)]),
        status_id: new FormControl('-1', [Validators.required, Validators.min(0)]),
        directie_id: new FormControl(this.dgaspc_id, [Validators.required, Validators.min(0)]),
        indoc_name: new FormControl('', []),
        indoc_id: new FormControl('', []),
        compartiment_id: new FormControl(null),
        user_id: new FormControl(null),
        observations: new FormControl(''),
    });

    parameters: { key: string; value: string }[] = [
        { key: '--Alegeți--', value: '-1' },
        { key: 'Da', value: '1' },
        { key: 'Nu', value: '0' },
    ];

    defaultParameter: { name: string; id: string } = {
        name: '--Alegeți--',
        id: '-1',
    };

    statusesActive$!: Observable<Generic[]>;
    prioritiesActive$!: Observable<Generic[]>;

    private destroy$ = new Subject<void>();

    directiiActive: Generic[] = [];
    filteredDirectii: Generic[] = [];
    directiiControl = new FormControl();

    compartimenteActive: Generic[] = [];
    filteredCompartimente: Generic[] = [];
    compartimenteControl = new FormControl();

    usersActive: Generic[] = [];
    filteredUsers: Generic[] = [];
    usersControl = new FormControl();

    constructor(
        private dialogRef: MatDialogRef<AddCerereTypeComponent>,
        private requestTypesService: CerereTypeService,
        private notificationService: NotificationService,
        private commonService: CommonService,
        private authService: AuthService,
        private cererePriorityService: CererePriorityService,
        private statusService: StatusService
    ) {
        this.processToken();
    }

    ngOnInit() {
        this.loadStatususesActive();
        this.loadPrioritiesActive();
        this.loadDirectiiActive();

        this.loadCompartimenteActive(this.dgaspc_id);

        this.loadUsersActive();

        this.renderFieldsSearch();
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    closeDialog() {
        this.dialogRef?.close({
            success: false,
        });
    }

    processToken() {
        const tokenObj = this.authService.returnTokenUser();
        tokenObj.then((token) => {
            this.token = token;
            this.requestTypesService.setToken(token);
        });
    }

    submitForm() {
        this.isLoading = true;

        const {
            name,
            slug,
            priority_id,
            status_id,
            directie_id,
            compartiment_id,
            user_id,
            observations,
            indoc_name,
            indoc_id,
        } = this.form.value as CerereTypeAddPayload;
        const newCerereType: CerereTypeAddPayload = {
            name,
            slug,
            priority_id,
            status_id,
            directie_id,
            compartiment_id,
            user_id,
            observations,
            indoc_name,
            indoc_id,
        };

        this.requestTypesService
            .addRequestType(newCerereType)
            .pipe(finalize(() => (this.isLoading = false)))
            .subscribe({
                next: (res) => {
                    this.dialogRef.close({ success: true });
                },
                error: (err) => {
                    let msg;
                    try {
                        // try to catch the message from server
                        msg = err.error.errors.message[0];
                    } catch (error) {
                        // controll output message
                        msg = helper.dialogConfig.generalMessages.error;
                    }
                    this.notificationService.warningSwal(
                        this.errorTitle,
                        msg,
                        this.errorIcon
                    );
                },
            });
    }

    loadStatususesActive() {
        this.isLoading = true;
        this.statusesActive$ = this.commonService.getCerereStatusesActive().pipe(
            map((results: any) => [this.defaultParameter, ...results]),
            finalize(() => (this.isLoading = false))
        );
    }

    loadPrioritiesActive() {
        this.isLoading = true;
        this.prioritiesActive$ = this.commonService
            .getCererePrioritiesActive()
            .pipe(
                map((results: any) => [this.defaultParameter, ...results]),
                finalize(() => (this.isLoading = false))
            );
    }

    renderFieldsSearch() {
        this.directiiControl.valueChanges.pipe(
            takeUntil(this.destroy$),
            distinctUntilChanged(),
            switchMap((inputValue) => {
                if (inputValue.length < 1) {
                    this.filteredDirectii = this.directiiActive;
                    return of(this.directiiActive);
                }
                return this.filterDirectii(inputValue);
            }),
            tap((data: any[]) => {
                this.filteredDirectii = data;
            }),
            catchError(() => of([]))
        ).subscribe();

        this.compartimenteControl.valueChanges.pipe(
            takeUntil(this.destroy$),
            distinctUntilChanged(),
            switchMap((inputValue) => {
                if (inputValue.length < 1) {
                    this.filteredCompartimente = this.compartimenteActive;
                    return of(this.compartimenteActive);
                }
                return this.filterCompartimente(inputValue);
            }),
            tap((data: any[]) => {
                this.filteredCompartimente = data;
            }),
            catchError(() => of([]))
        ).subscribe();

        this.usersControl.valueChanges.pipe(
            takeUntil(this.destroy$),
            distinctUntilChanged(),
            switchMap((inputValue) => {
                if (inputValue.length < 1) {
                    this.filteredUsers = this.usersActive;
                    return of(this.usersActive);
                }
                return this.filterOperatori(inputValue);
            }),
            tap((data: any[]) => {
                this.filteredUsers = data;
            }),
            catchError(() => of([]))
        ).subscribe();
    }

    loadDirectiiActive() {
        this.isLoading = true;
        this.commonService
            .getDirectiiActive()
            .pipe(
                map((results: any) => [this.defaultParameter, ...results]),
                finalize(() => this.isLoading = false)
            )
            .subscribe({
                next: (results) => {
                    this.directiiActive = results;
                    this.filteredDirectii = [...this.directiiActive];
                },
                error: (error) => {
                    console.error('Error loading directii', error);
                    this.filteredDirectii = [];
                },
            });
    }

    onDirectieClosed() {
        const selectedValue = this.directiiControl.value;

        if (selectedValue) {
            const isSelectedInFilteredList = this.filteredDirectii.some(directie =>
                directie.id === this.form.get('directie_id')?.value
            );

            if (!isSelectedInFilteredList) {
                this.directiiControl.setValue(null);
                this.filteredDirectii = this.directiiActive;
                this.destroy$.next();
                this.destroy$.complete();
                this.renderFieldsSearch();
            }
        }
    }

    onDirectieChange(directieId: number): void {
        this.form.controls['compartiment_id'].patchValue(-1);
        this.form.controls['user_id'].patchValue(-1);

        if (directieId > 0) {
            this.loadCompartimenteActive(directieId);
            this.loadUsersActive();
        } else {
            this.compartimenteActive = [];
            this.usersActive = [];
            this.filteredCompartimente = [];
            this.filteredUsers = [];
        }
    }

    loadCompartimenteActive(directieId?: number) {
        this.isLoading = true;

        let compartimenteObservable$ = directieId
            ? this.commonService.getCompartimenteByDirectieId(directieId)
            : this.commonService.getCompartimenteActive();

        compartimenteObservable$
            .pipe(
                map((results: any) => [...results]),
                finalize(() => { this.isLoading = false })
            ).subscribe({
                next: (data) => {
                    this.compartimenteActive = [...data];
                    this.filteredCompartimente = [...this.compartimenteActive];
                },
                error: (error) => {
                    console.error(error);
                    this.filteredCompartimente = [];
                },
            });
    }

    onDirectieCompartimentClosed() {
        const selectedValue = this.compartimenteControl.value;

        if (selectedValue) {
            const isSelectedInFilteredList = this.filteredCompartimente.some(compartiment =>
                compartiment.id === this.form.get('compartiment_id')?.value
            );

            if (!isSelectedInFilteredList) {
                this.compartimenteControl.setValue(null);
                this.filteredCompartimente = this.compartimenteActive;
                this.destroy$.next();
                this.destroy$.complete();
                this.renderFieldsSearch();
            }
        }
    }

    onDirectieCompartimentChange(compartimentId: number): void {
        this.form.controls['user_id'].patchValue(-1);

        if (compartimentId > 0) {
            this.loadUsersActive();
        } else {
            this.usersActive = [];
            this.filteredUsers = [];
        }
    }

    loadUsersActive() {
        this.isLoading = true;
        const payload: UserOrganigramaSearchPayload = {
            directie_id: this.form.controls['directie_id'].value,
            compartiment_id: this.form.controls['compartiment_id'].value,
        };

        this.commonService
            .getUsersByOrganigrama(payload)
            .pipe(
                map((results: any) => [...results]),
                finalize(() => { this.isLoading = false })
            ).subscribe({
                next: (data) => {
                    this.usersActive = [...data];
                    this.filteredUsers = [...this.usersActive];
                },
                error: (error) => {
                    console.error(error);
                    this.filteredUsers = [];
                },
            });
    }

    onUsersActiveClosed() {
        const selectedValue = this.usersControl.value;

        if (selectedValue) {
            const isSelectedInFilteredList = this.filteredUsers.some(operator =>
                operator.id === this.form.get('user_id')?.value
            );

            if (!isSelectedInFilteredList) {
                this.usersControl.setValue(null);
                this.filteredUsers = this.usersActive;
                this.destroy$.next();
                this.destroy$.complete();
                this.renderFieldsSearch();
            }
        }
    }

    normalizeString(value: string): string {
        return value.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
    }

    filterDirectii(searchTerm: string): Observable<any[]> {
        const normalizedSearchTerm = this.normalizeString(searchTerm);

        const filtered = this.directiiActive.filter(directie =>
            this.normalizeString(directie.name).includes(normalizedSearchTerm)
        );

        return of(filtered);
    }

    filterCompartimente(searchTerm: string): Observable<any[]> {
        const normalizedSearchTerm = this.normalizeString(searchTerm);

        const filtered = this.compartimenteActive.filter(compartiment =>
            this.normalizeString(compartiment.name).includes(normalizedSearchTerm)
        );

        return of(filtered);
    }

    filterOperatori(searchTerm: string): Observable<any[]> {
        const normalizedSearchTerm = this.normalizeString(searchTerm);

        const filtered = this.usersActive.filter(user =>
            this.normalizeString(user.name).includes(normalizedSearchTerm)
        );

        return of(filtered);
    }
}
